import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import {
  inspectPhotoMetadata,
  MAX_METADATA_FILE_BYTES,
  MetadataInspectionError,
  type MetadataAvailability,
  type MetadataGroup,
  type MetadataInspection,
  type MetadataInspectionErrorCode,
} from '@/features/photo-metadata-viewer'
import { useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type Copy = Translations['metadataViewer']
type Status = 'idle' | 'processing' | 'ready' | 'error'

const ANALYTICS_CONTEXT = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/posmotret-exif',
  utility: 'metadata-viewer',
  processingMode: 'local',
} satisfies ToolAnalyticsContext

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.tif', '.tiff', '.heic', '.heif']

export function MetadataViewerPage() {
  const t = useT()
  const copy = t.metadataViewer
  const [status, setStatus] = useState<Status>('idle')
  const [result, setResult] = useState<MetadataInspection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const controllerRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      controllerRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    controllerRef.current?.abort()
    controllerRef.current = null
    setStatus('idle')
    setResult(null)
    setError(null)
    setCopied(false)
  }, [])

  const inspect = useCallback(async (file: File) => {
    const generation = ++generationRef.current
    controllerRef.current?.abort()
    controllerRef.current = null
    const validation = validateFile(file)
    if (validation) {
      trackToolRejection(ANALYTICS_CONTEXT, mapAnalyticsError(validation))
      setStatus('error')
      setResult(null)
      setError(errorMessage(validation, copy))
      return
    }
    const controller = new AbortController()
    controllerRef.current = controller
    setStatus('processing')
    setResult(null)
    setError(null)
    setCopied(false)
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const startedAt = performance.now()
    try {
      const inspection = await inspectPhotoMetadata(await file.arrayBuffer(), controller.signal)
      if (!mountedRef.current || generation !== generationRef.current) return
      setResult(inspection)
      setStatus('ready')
      operation.success(performance.now() - startedAt)
    } catch (caught) {
      if (
        !mountedRef.current
        || generation !== generationRef.current
        || (caught instanceof DOMException && caught.name === 'AbortError')
      ) return
      const code = caught instanceof MetadataInspectionError ? caught.code : 'processing_failed'
      setStatus('error')
      setError(errorMessage(code, copy))
      operation.error(mapAnalyticsError(code))
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null
    }
  }, [copy])

  const coordinates = useMemo(() => result ? findCoordinates(result.groups) : null, [result])

  const copyJson = useCallback(async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(result.json, null, 2))
      setCopied(true)
    } catch {
      setCopied(false)
      setError(copy.errorsText.copy)
    }
  }, [copy.errorsText.copy, result])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['JPG, TIFF, HEIC', t.common.maxFileSize]}
      />

      <Card
        className={cn(
          'mx-auto max-w-3xl transition-colors',
          dropActive && 'border-primary bg-primary/5',
        )}
        onDragOver={(event) => {
          event.preventDefault()
          setDropActive(true)
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDropActive(false)
          const file = event.dataTransfer.files[0]
          if (file) void inspect(file)
        }}
      >
        <CardContent className="p-4 text-center sm:p-8">
          <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
            EXIF
          </div>
          <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
          <p className="mx-auto mb-5 max-w-lg text-sm leading-6 text-muted-foreground">
            {copy.dropDescription}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.tif,.tiff,.heic,.heif,image/jpeg,image/tiff,image/heic,image/heif"
            className="hidden"
            disabled={status === 'processing'}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void inspect(file)
              event.target.value = ''
            }}
          />
          <div className="flex flex-wrap justify-center gap-3">
            <Button disabled={status === 'processing'} onClick={() => inputRef.current?.click()}>
              {status === 'processing' ? copy.inspecting : copy.chooseFile}
            </Button>
            {status === 'processing' && (
              <Button variant="outline" onClick={reset}>{copy.cancel}</Button>
            )}
            {status !== 'processing' && (result || error) && (
              <Button variant="outline" onClick={reset}>{copy.reset}</Button>
            )}
          </div>
          {error && (
            <p className="mx-auto mt-5 max-w-lg rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>
      </Card>

      <p className="mx-auto mt-3 max-w-2xl text-xs leading-5 text-muted-foreground sm:text-center">
        {copy.privacyNote}
      </p>

      {result && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <div className="min-w-0 space-y-5">
            <Card>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold tracking-normal">{copy.resultsTitle}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {copy.formatLabel}: {formatName(result.format)}
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => void copyJson()}>
                    {copied ? copy.copied : copy.copyJson}
                  </Button>
                </div>
                <div className="mt-5 grid gap-2 sm:grid-cols-3">
                  {(['exif', 'iptc', 'xmp'] as const).map((kind) => (
                    <div className="flex justify-between gap-3 rounded-md border px-3 py-2 text-sm" key={kind}>
                      <span>{kind.toUpperCase()}</span>
                      <strong className="text-right">{availabilityLabel(result.availability[kind], copy)}</strong>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {result.groups.map((group) => (
              <Card key={group.id}>
                <CardContent className="p-5">
                  <h2 className="text-lg font-semibold tracking-normal">{copy.groups[group.id]}</h2>
                  <dl className="mt-4 divide-y">
                    {group.values.map((value, index) => (
                      <div className="grid gap-1 py-3 text-sm sm:grid-cols-[minmax(140px,0.4fr)_1fr] sm:gap-4" key={`${value.key}-${index}`}>
                        <dt className="break-words text-muted-foreground">{value.label}</dt>
                        <dd className="break-all font-medium sm:text-right">{String(value.value)}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            ))}
            {result.groups.length === 0 && (
              <p className="rounded-md border bg-card p-5 text-sm text-muted-foreground">
                {copy.noMetadata}
              </p>
            )}
          </div>

          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-4 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{copy.privacyTitle}</h2>
                {result.privacyFlags.length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {result.privacyFlags.map((flag) => (
                      <li className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2" key={flag}>
                        {copy.privacyFlags[flag]}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">{copy.noPrivacyFlags}</p>
                )}
                {coordinates && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      const latitude = encodeURIComponent(coordinates.latitude)
                      const longitude = encodeURIComponent(coordinates.longitude)
                      window.open(
                        `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }}
                  >
                    {copy.openMap}
                  </Button>
                )}
                <Button asChild className="w-full">
                  <a href="/udalit-exif">{copy.removeExifCta}</a>
                </Button>
              </CardContent>
            </Card>
            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        </div>
      )}

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
          </div>
        ))}
      </section>
    </div>
  )
}

function validateFile(file: File): MetadataInspectionErrorCode | null {
  if (file.size > MAX_METADATA_FILE_BYTES) return 'size_limit'
  const name = file.name.toLowerCase()
  if (!ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))) return 'unsupported_input'
  return null
}

function errorMessage(code: MetadataInspectionErrorCode, copy: Copy): string {
  if (code === 'size_limit') return copy.errorsText.tooLarge
  if (code === 'unsupported_input') return copy.errorsText.unsupported
  if (code === 'invalid_input') return copy.errorsText.invalid
  return copy.errorsText.failed
}

function mapAnalyticsError(code: MetadataInspectionErrorCode): ToolErrorCode {
  if (code === 'size_limit') return 'size_limit'
  if (code === 'unsupported_input') return 'unsupported_input'
  if (code === 'invalid_input') return 'invalid_input'
  return 'processing_failed'
}

function availabilityLabel(value: MetadataAvailability, copy: Copy): string {
  if (value === 'available') return copy.available
  if (value === 'unsupported') return copy.unsupported
  return copy.notFound
}

function formatName(format: MetadataInspection['format']): string {
  if (format === 'jpeg') return 'JPG'
  return format.toUpperCase()
}

function findCoordinates(
  groups: MetadataGroup[],
): { latitude: string; longitude: string } | null {
  const gps = groups.find((group) => group.id === 'gps')
  if (!gps) return null
  const latitude = gps.values.find((value) => /latitude/.test(value.key))
  const longitude = gps.values.find((value) => /longitude/.test(value.key))
  return latitude && longitude
    ? { latitude: String(latitude.value), longitude: String(longitude.value) }
    : null
}
